import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { Outlet, useNavigate } from 'react-router-dom';
import Loader from 'components/Loader';
import { Bar } from 'components/Header/Bar';
import { getPathWithRef } from 'helpers/utils';
import { trackEvent } from 'helpers/mixpanelEvents';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { useRedirectAfterLogin } from 'hooks/useRedirectAfterLogin';
import { getUserState } from 'selectors/user';
import { getPagesState } from 'selectors/pages';
import { setIsUserOnboarded, setUserName } from 'services/storage';
import { getPages } from 'store/pages/actions';
import { getUserProfile } from 'store/user/actions';
import { getUserStripeSubscriptions } from 'store/userStripeSubscriptions/actions';
import { getTransactions } from 'store/transactions/actions';
import { getAllContributionSummaries } from 'store/contributionSummary/slice';
import { getRecommendations } from 'store/recommendations/actions';
import { datadogLogs } from '@datadog/browser-logs';
import { useAuth } from 'hooks/auth';
import { getSubscriptions } from 'store/subscriptions/actions';
import { getMessageFromAxiosError } from 'helpers/errors';
import { getImpactReport } from 'store/impactReport/actions';
import { useGateValue } from '@statsig/react-bindings';
import { SHOW_IMPACT_REPORT_GATE } from 'constants/statsig';
import { CURRENT_CYCLE_YEAR } from './utils';

// const UNAUTHORIZED_ERROR_STATUS = 401;
const SKIP_ONBOARDING_REDIRECT = true;

export const ProfileScreen = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, isAuthenticated } = useAuth0();
  const auth0Requested = useAuth();
  const [isAllDataRequested, setIsAllDataRequested] = useState(false);
  const { screenToRedirect, handleRedirect } = useRedirectAfterLogin();
  const { userState, pagesState } = useAppSelector(state => ({
    userState: getUserState(state),
    pagesState: getPagesState(state),
  }));
  const showImpactReport = useGateValue(SHOW_IMPACT_REPORT_GATE);

  const handleApiError = (error: AxiosError) => {
    // if (error.response?.status === UNAUTHORIZED_ERROR_STATUS) {
    //   navigate('/login');
    //   return;
    // }
    // navigate('/error');
    // THIS WAS CAUSING A BUG FOR NEW USERS GETTING ERROR RESPONSE
    // FROM GET /recommendations
    // HANDLE ERROR SILENTLY FOR NOW
    console.warn(error.message);
    datadogLogs.logger.warn(
      'Browser error occurred on profile page',
      {},
      new Error(getMessageFromAxiosError(error))
    );
    return;
  };

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (!isAuthenticated) {
      navigate(getPathWithRef('/login'));
      return;
    }
    if (screenToRedirect) {
      handleRedirect(screenToRedirect);
      return;
    }
    if (!userState.data) {
      return;
    }
    if (!SKIP_ONBOARDING_REDIRECT && !userState.data.is_onboarded) {
      navigate(getPathWithRef('/questions'));
      return;
    }
    if (userState?.data?.id) {
      trackEvent('Profile Page Viewed', {
        user_id: userState?.data?.id,
      });
      return;
    }
  }, [isLoading, isAuthenticated, userState.data]);

  useEffect(() => {
    if (auth0Requested && isAuthenticated) {
      Promise.allSettled([
        dispatch(getPages()),
        dispatch(getRecommendations()),
        dispatch(getUserProfile()),
      ]).catch(error => {
        handleApiError(error);
      });
    }
  }, [isAuthenticated, auth0Requested]);

  useEffect(() => {
    if (auth0Requested && isAuthenticated && showImpactReport) {
      dispatch(getImpactReport(CURRENT_CYCLE_YEAR)).catch(error => {
        handleApiError(error);
      });
    }
  }, [isAuthenticated, auth0Requested, showImpactReport]);

  useEffect(() => {
    if (!userState.isLoading && userState.data && !isAllDataRequested) {
      const isOnboarded = userState.data.is_onboarded;
      dispatch(getTransactions()).catch(error => {
        handleApiError(error);
      });
      dispatch(getSubscriptions()).catch(error => {
        handleApiError(error);
      });
      // Cycle giving per recipient for the profile "This Cycle" view
      dispatch(getAllContributionSummaries());
      if (userState.data.stripe_customer_id) {
        dispatch(getUserStripeSubscriptions()).catch(error => {
          handleApiError(error);
        });
      }
      setIsAllDataRequested(true);
      setIsUserOnboarded(isOnboarded.toString());
      setUserName(userState.data?.first_name);
      if (!SKIP_ONBOARDING_REDIRECT && !isOnboarded) navigate(getPathWithRef('/questions'));
    }
  }, [userState]);

  return (
    <>
      <Bar />
      {!userState.data || !pagesState.data || isLoading ? (
        <div className="loader-center">
          <Loader width={60} height={60} />
        </div>
      ) : (
        <Outlet />
      )}
    </>
  );
};
